import { AlertTriangle, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { usePlanSelectDialog } from "@/contexts/PlanSelectDialogContext";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

interface UpgradePromptProps {
  feature: string;
  message?: string;
  variant?: "inline" | "card" | "alert";
  className?: string;
}

export function UpgradePrompt({ feature, message, variant = "alert", className }: UpgradePromptProps) {
  const { t } = useTranslation();
  const { openDialog } = usePlanSelectDialog();

  const defaultMessage = t('subscription.upgrade.defaultMessage', 
    'This feature is not available on your current plan. Upgrade to access {{feature}}.', 
    { feature }
  );

  if (variant === "inline") {
    return (
      <div className={cn("flex items-center gap-2 text-sm text-muted-foreground", className)}>
        <AlertTriangle className="h-4 w-4 text-yellow-500" />
        <span>{message || defaultMessage}</span>
        <Button variant="link" size="sm" className="h-auto p-0" onClick={openDialog}>
          {t('subscription.upgrade.viewPlans', 'View Plans')}
        </Button>
      </div>
    );
  }

  if (variant === "card") {
    return (
      <div className={cn(
        "flex flex-col items-center justify-center p-6 rounded-lg border border-dashed border-muted-foreground/30 bg-muted/30 text-center",
        className
      )}>
        <Zap className="h-10 w-10 text-primary mb-3" />
        <h3 className="font-semibold mb-1">{t('subscription.upgrade.title', 'Upgrade Required')}</h3>
        <p className="text-sm text-muted-foreground mb-4 max-w-sm">
          {message || defaultMessage}
        </p>
        <Button onClick={openDialog}>
          {t('subscription.upgrade.viewPlans', 'View Plans')}
        </Button>
      </div>
    );
  }

  return (
    <Alert className={cn("border-yellow-500/50 bg-yellow-500/10", className)}>
      <AlertTriangle className="h-4 w-4 text-yellow-500" />
      <AlertTitle>{t('subscription.upgrade.title', 'Upgrade Required')}</AlertTitle>
      <AlertDescription className="flex items-center justify-between">
        <span>{message || defaultMessage}</span>
        <Button variant="outline" size="sm" onClick={openDialog}>
          {t('subscription.upgrade.viewPlans', 'View Plans')}
        </Button>
      </AlertDescription>
    </Alert>
  );
}

interface LimitWarningProps {
  percentUsed: number;
  limitName: string;
  used: number;
  limit: number | null;
  className?: string;
}

export function LimitWarning({ percentUsed, limitName, used, limit, className }: LimitWarningProps) {
  const { t } = useTranslation();
  const { openDialog } = usePlanSelectDialog();

  if (percentUsed < 80 || limit === null) return null;

  const isCritical = percentUsed >= 100;

  return (
    <Alert 
      className={cn(
        isCritical ? "border-destructive/50 bg-destructive/10" : "border-yellow-500/50 bg-yellow-500/10",
        className
      )}
    >
      <AlertTriangle className={cn("h-4 w-4", isCritical ? "text-destructive" : "text-yellow-500")} />
      <AlertTitle>
        {isCritical 
          ? t('subscription.limits.reachedTitle', '{{limitName}} Limit Reached', { limitName })
          : t('subscription.limits.approachingTitle', 'Approaching {{limitName}} Limit', { limitName })
        }
      </AlertTitle>
      <AlertDescription className="flex items-center justify-between">
        <span>
          {isCritical 
            ? t('subscription.limits.reachedMessage', 'You have used {{used}} of {{limit}} {{limitName}}. Upgrade to continue.', { used, limit, limitName })
            : t('subscription.limits.approachingMessage', 'You have used {{used}} of {{limit}} {{limitName}} ({{percent}}%).', { used, limit, limitName, percent: Math.round(percentUsed) })
          }
        </span>
        <Button variant="outline" size="sm" onClick={openDialog}>
          {t('subscription.upgrade.viewPlans', 'View Plans')}
        </Button>
      </AlertDescription>
    </Alert>
  );
}
